/**
 * Sysadmin Platform — Surface B layout (Wave 1a / W1a-15+).
 *
 * Mounted at `/sysadmin/*`. Only reachable when the active token has
 * audience `argusai-admin` (sysadmin context).
 *
 * This layout is now a thin Outlet wrapper + audience-gate fallback. The
 * original W1a-15 top sub-tab bar (Overview/Administrators/Tenants/Models)
 * was REMOVED: it predated the left nav-rail and duplicated it (Tenants +
 * Administrators lived in both, Models lived in both the top bar and the
 * Catalog sub-bar), stacking a redundant second row on every sysadmin page.
 * The left nav-rail (nav-rail.tsx SYSADMIN_RAIL_ITEMS) is the single primary
 * nav; per-section sub-tabs (e.g. Catalog's entity tabs) render in their own
 * child layouts where relevant.
 *
 * The component itself does NOT enforce the audience gate — that's done
 * server-side. The layout shows a friendly "Sysadmin context required"
 * panel if a tenant user tries to navigate here directly.
 */

import { createFileRoute, Outlet } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';

export const Route = createFileRoute('/sysadmin')({
  component: PlatformAdminLayout,
});

function PlatformAdminLayout() {
  const { isSysadmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!isSysadmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 px-6">
        <ShieldCheck className="h-10 w-10 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Sysadmin context required</h2>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          The platform-admin surface is only available when signed in with the
          sysadmin context. Sign out and choose the sysadmin context at the
          login screen.
        </p>
      </div>
    );
  }

  // No top sub-tab bar here anymore. The left nav-rail (nav-rail.tsx
  // SYSADMIN_RAIL_ITEMS) is the single primary nav — Overview→Home,
  // Administrators, Tenants, and Models→Catalog are all reachable there, so
  // the old top bar (W1a-15, predates the rail) only duplicated it and stacked
  // a redundant second row on every sysadmin page. Catalog/etc. still render
  // their own contextual sub-tab bar (catalog.tsx) where relevant.
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
